import { useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';

import doesSpreadHasMissingImages from 'editor/src/store/design/selector/doesSpreadHasMissingImages';
import { ContentAddress, Spread, SpreadGroup } from 'editor/src/store/design/types';
import loadFontOperation from 'editor/src/store/fonts/operation/loadFontOperation';
import { useDispatch, useSelector, useStore } from 'editor/src/store/hooks';
import getAreaTokenForSpreadName from 'editor/src/store/livePreview/selector/getAreaTokenForSpreadName';

import useIsMounted from 'editor/src/util/useIsMounted';

import { createSpreadPreview, RequestRenderFn, SpreadPreviewDataURL } from 'editor/src/component/SpreadPreview';
import {
  PreviewOutput,
  SpreadPreviewBlob,
  SpreadPreviewCanvas,
  SpreadPreviewOptions,
} from 'editor/src/component/SpreadPreview/createSpreadPreview';
import { useIsMobile } from 'editor/src/component/useDetectDeviceType';

import coversPrintArea from './coversPrintArea';
import findSpreadGroundImage from './findSpreadGroundImage';
import getGroupedSpreadInfo from './getGroupedSpreadInfo';

function useSpreadPreview<Type extends PreviewOutput = 'dataURL'>(
  requestRender: RequestRenderFn,
  spread: Spread | undefined,
  designKey: string,
  spreadIndex: number,
  size: { dimension: 'height' | 'width' | 'both'; value: number },
  options: SpreadPreviewOptions<Type>,
  focusedContent?: ContentAddress,
  spreadGroup?: SpreadGroup,
  skipIsMounted?: boolean,
) {
  const store = useStore();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const {
    spreadBackgrounds,
    spreadForegrounds,
    spreadMasks,
    gridDesigns,
    images,
    addons,
    digitizedAssets,
    calendar,
    areaToken,
  } = useSelector((state) => {
    const spreadImages = state.variants.spreadImages[designKey];
    return {
      spreadBackgrounds: spreadImages ? spreadImages.spreadBackgrounds : state.design.spreadBackgrounds,
      spreadForegrounds: spreadImages ? spreadImages.spreadForegrounds : state.design.spreadForegrounds,
      spreadMasks: state.design.spreadMasks,
      gridDesigns: state.gridDesigns.grids,
      images: state.gallery.images,
      addons: state.editorModules.addons.inUse,
      digitizedAssets: state.design.digitizedAssets,
      calendar: state.design.designData?.calendar,
      // In the deps so previews recompute when the token order arrives
      // (the live-preview payload can land after the spread grounds).
      areaToken: getAreaTokenForSpreadName(state, spread?.name),
    };
  }, shallowEqual);
  const [preview, setPreview] =
    useState<
      Type extends 'blob' ? SpreadPreviewBlob : Type extends 'canvas' ? SpreadPreviewCanvas : SpreadPreviewDataURL
    >();

  const hasMissingImages = useMemo(() => doesSpreadHasMissingImages(spread, images), [spread, images]);
  const isMounted = skipIsMounted ? () => true : useIsMounted();

  function loadFont(fontFamily: string) {
    return dispatch(loadFontOperation(fontFamily));
  }

  // Reset the stale preview during render, before this render's request is
  // queued below. A render that finds every ground already cached resolves in
  // microtasks, ahead of React's passive effects — an effect-based reset then
  // runs after the new preview has landed and wipes it.
  const [previewSpreadIndex, setPreviewSpreadIndex] = useState(spreadIndex);
  if (!skipIsMounted && previewSpreadIndex !== spreadIndex) {
    setPreviewSpreadIndex(spreadIndex);
    setPreview(undefined);
  }

  const version = useRef(0);
  const currentVersion = version.current;
  useMemo(() => {
    if (!spread) {
      return;
    }

    version.current += 1;
    // GAPI-30848 — tech-suffix-insensitive lookup. See
    // `findSpreadGroundImage` comment. Without this fallback the variant
    // thumbnails and bottom-strip tiles render with no garment background
    // for any multi-tech spread whose tech doesn't match the catalogue's
    // stored scene name (typically: DTG spreads get no background because
    // the catalogue only stores DTF scenes per area).
    const foregroundImage =
      findSpreadGroundImage(spreadForegrounds, spread.name) ?? findSpreadGroundImage(spreadForegrounds, areaToken);
    const backgroundImage =
      findSpreadGroundImage(spreadBackgrounds, spread.name) ?? findSpreadGroundImage(spreadBackgrounds, areaToken);
    const foundMask = findSpreadGroundImage(spreadMasks, spread.name) ?? findSpreadGroundImage(spreadMasks, areaToken);
    // Same rule as the canvas: a mask that does not cover the print area only
    // marks the face its photo shows, so it must not clip the artwork.
    const maskImage = foundMask && coversPrintArea(foundMask) ? foundMask : undefined;

    const spreads = store.getState().design.designData?.spreads;
    const spreadGroupInfo =
      spreads && spreadGroup
        ? getGroupedSpreadInfo(spreadGroup, spreads, spreadBackgrounds, spreadForegrounds)
        : undefined;

    void createSpreadPreview(
      designKey,
      spread,
      {
        backgroundImage,
        foregroundImage,
        maskImage,
        gridDesigns,
        images,
        addons,
      },
      spreadIndex,
      focusedContent,
      size,
      requestRender,
      digitizedAssets,
      loadFont,
      isMobile,
      t,
      // A shaped spread's grounds are drawn edge to edge; the preview needs a
      // margin so the outline doesn't sit on the viewport border.
      { ...options, padContent: !!maskImage },
      spreadGroup,
      spreadGroupInfo,
      calendar,
    ).then((preview) => {
      if (isMounted() && preview && currentVersion + 1 === version.current) {
        setPreview(preview as any);
      }
    });
  }, [
    spread,
    focusedContent,
    hasMissingImages,
    addons,
    designKey,
    spreadBackgrounds,
    spreadForegrounds,
    spreadMasks,
    spreadGroup,
    areaToken,
  ]);

  return { preview, isRendering: currentVersion !== version.current };
}

export default useSpreadPreview;
